import { TriggerNodeDataDto } from "@babackapp/private-api-client/types";
import type { ReturnWorkflowTriggerType } from "@babackapp/return-variables-registry";
import { ClaimStatusChangeTriggerConfigDto } from "../node-types";

// Extended trigger type until API client is regenerated (claim), plus the
// return-domain triggers served by the dedicated return-workflows engine.
type ExtendedTriggerType =
  | TriggerNodeDataDto["triggerType"]
  | "claim_status_change"
  | ReturnWorkflowTriggerType;

/** Tracking domain trigger set (wired as availableTriggerTypes by the tracking config). */
export const TRIGGER_TYPE_OPTIONS: ExtendedTriggerType[] = [
  "tracking_status_change",
  "order_not_fulfilled",
  "order_created",
  "click_and_collect_status_change",
  "claim_status_change",
];

/**
 * Return domain trigger set (wired as availableTriggerTypes by the return
 * config, BAB-2854). The trigger type IS the event — empty configs.
 */
export const RETURN_TRIGGER_TYPE_OPTIONS: readonly ReturnWorkflowTriggerType[] = [
  "return_created",
  "return_cancelled",
  "return_need_approval",
  "return_approved",
  "return_merchant_approved",
  "return_merchant_rejected",
  "return_shipping_label_created",
  "return_shipping_label_error",
  "return_delivered",
  "return_discount_created",
  "return_payment_refunded",
  "return_deposit_reminder",
  "return_resolution_updated",
  "return_logistician_approved",
  "return_logistician_rejected",
  "return_treatment_error",
];

export function isReturnTriggerType(
  triggerType: string,
): triggerType is ReturnWorkflowTriggerType {
  return (RETURN_TRIGGER_TYPE_OPTIONS as readonly string[]).includes(
    triggerType,
  );
}

type ExtendedTriggerConfigs = Record<
  TriggerNodeDataDto["triggerType"],
  TriggerNodeDataDto["config"]
> & {
  claim_status_change: ClaimStatusChangeTriggerConfigDto;
} & Record<ReturnWorkflowTriggerType, Record<string, never>>;

export const DEFAULT_TRIGGER_CONFIGS: ExtendedTriggerConfigs = {
  tracking_status_change: {
    trackingStatus: "IN_TRANSIT",
  },
  order_not_fulfilled: {
    minDaysSinceCreation: 3,
  },
  order_created: {},
  // Default to PRETE_AU_RETRAIT (the typical "order ready" workflow). The
  // editor panel lets the merchant switch to RETIREE for post-pickup
  // workflows (review request, thank-you email, etc.).
  click_and_collect_status_change: {
    status: "PRETE_AU_RETRAIT",
  },
  claim_status_change: {
    claimStatuses: ["NEW"],
  },
  // Return triggers: the type is the event, nothing to configure.
  return_created: {},
  return_cancelled: {},
  return_need_approval: {},
  return_approved: {},
  return_merchant_approved: {},
  return_merchant_rejected: {},
  return_shipping_label_created: {},
  return_shipping_label_error: {},
  return_discount_created: {},
  return_payment_refunded: {},
  return_deposit_reminder: {},
  return_resolution_updated: {},
  return_logistician_approved: {},
  return_logistician_rejected: {},
  return_treatment_error: {},
  return_delivered: {},
};

export const TRIGGER_NODE_WIDTH = 250;
export const TRIGGER_NODE_HEIGHT_ESTIMATION = 120;

export function buildTriggerNode(
  triggerType: TriggerNodeDataDto["triggerType"],
  config: TriggerNodeDataDto["config"],
) {
  return {
    id: crypto.randomUUID(),
    type: "trigger" as const,
    data: {
      label: "trigger",
      triggerType,
      config,
    },
  };
}
